import React, { useState, useEffect } from 'react';

const WeatherTab = ({ API, adminToken, isDarkMode, showNotification: externalNotify }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    default_city: 'Casablanca',
    show_for_free_users: true,
    show_for_logged_in_only: true,
    cache_minutes: 30,
    city_abbreviations: {},
    weather_api: 'open-meteo'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testCity, setTestCity] = useState('Casablanca');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState(null);

  const cities = [
    'Agadir', 'Al Hoceima', 'Asilah', 'Azrou', 'Beni Ansar', 'Beni Mellal',
    'Berkane', 'Berrechid', 'Casablanca', 'Chefchaouen', 'Dakhla', 'El Jadida',
    'Errachidia', 'Essaouira', 'Fes', 'Fnideq', 'Guelmim', 'Ifrane',
    'Imlil', 'Inezgane', 'Jerada', 'Kenitra', 'Khemisset', 'Khouribga',
    'Laayoune', 'Larache', 'Marrakech', 'Mdiq', 'Meknes', 'Merzouga',
    'Midelt', 'Mohammedia', 'Nador', 'Ouarzazate', 'Oujda', 'Rabat',
    'Safi', 'Sale', 'Settat', 'Sidi Kacem', 'Sidi Slimane', 'Tafraout',
    'Tan-Tan', 'Tangier', 'Taourirt', 'Taroudant', 'Taza', 'Temara',
    'Tetouan', 'Tinghir', 'Tiznit', 'Zagora'
  ];

  // Built-in notification — never relies on browser alert()
  const notify = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
    // Also call external if provided (for consistency with app-wide notifications)
    if (externalNotify && typeof externalNotify === 'function') {
      try { externalNotify(message, type); } catch (e) { /* ignore */ }
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/weather/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setSettings(prev => ({ ...prev, ...data }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch weather settings:', err);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('enabled', settings.enabled);
      formData.append('default_city', settings.default_city);
      formData.append('show_for_free_users', settings.show_for_free_users);
      formData.append('show_for_logged_in_only', settings.show_for_logged_in_only);
      formData.append('cache_minutes', settings.cache_minutes);
      formData.append('weather_api', settings.weather_api);

      const res = await fetch(`${API}/admin/weather/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (res.ok) {
        notify('Weather settings saved!', 'success');
      } else {
        notify('Failed to save settings', 'error');
      }
    } catch (err) {
      notify('Error saving settings', 'error');
    }
    setSaving(false);
  };

  const testWeather = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/weather/${encodeURIComponent(testCity)}`);
      if (res.ok) {
        const data = await res.json();
        setTestResult(data);
      } else {
        setTestResult({ error: 'Failed to fetch weather' });
      }
    } catch (err) {
      setTestResult({ error: err.message });
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Built-in Toast Notification */}
      {toast && (
        <div className={`fixed top-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl border transition-all duration-300 animate-[slideIn_0.3s_ease-out] ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-300 text-green-800'
            : toast.type === 'error'
            ? 'bg-red-50 border-red-300 text-red-800'
            : 'bg-blue-50 border-blue-300 text-blue-800'
        }`}>
          <span className="text-xl">
            {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span className="font-medium text-sm">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 opacity-50 hover:opacity-100 text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            🌤️ Weather Settings
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Configure weather display for logged-in users
          </p>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          settings.enabled
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {settings.enabled ? '✓ Enabled' : '✗ Disabled'}
        </div>
      </div>

      {/* Main Settings Card */}
      <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        <div className="space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Enable Weather Feature
              </label>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Show weather in header for users
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                settings.enabled ? 'translate-x-7' : 'translate-x-0.5'
              }`}></div>
            </button>
          </div>

          {/* Default City */}
          <div>
            <label className={`block font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Default City
            </label>
            <select
              value={settings.default_city}
              onChange={(e) => setSettings(prev => ({ ...prev, default_city: e.target.value }))}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Used when user hasn't set their city yet
            </p>
          </div>

          {/* Show for Free Users */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.show_for_free_users}
              onChange={(e) => setSettings(prev => ({ ...prev, show_for_free_users: e.target.checked }))}
              className="w-5 h-5 rounded text-blue-600"
            />
            <div>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Show for Free Users
              </span>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Allow free plan users to see weather
              </p>
            </div>
          </label>

          {/* Show for Logged-in Only */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.show_for_logged_in_only}
              onChange={(e) => setSettings(prev => ({ ...prev, show_for_logged_in_only: e.target.checked }))}
              className="w-5 h-5 rounded text-blue-600"
            />
            <div>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Logged-in Users Only
              </span>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Only show weather to authenticated users
              </p>
            </div>
          </label>

          {/* Cache Duration */}
          <div>
            <label className={`block font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Cache Duration (minutes)
            </label>
            <input
              type="number"
              value={settings.cache_minutes}
              onChange={(e) => setSettings(prev => ({ ...prev, cache_minutes: parseInt(e.target.value) || 30 }))}
              min="5"
              max="120"
              className={`w-32 px-4 py-2 rounded-lg border ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              How long to cache weather data (reduces API calls)
            </p>
          </div>

          {/* Weather API Source */}
          <div>
            <label className={`block font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Weather API Source
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setSettings(prev => ({ ...prev, weather_api: 'open-meteo' }))}
                className={`flex-1 py-3 px-4 rounded-xl border-2 text-sm transition-all ${
                  settings.weather_api === 'open-meteo'
                    ? 'border-green-500 bg-green-50 text-black'
                    : 'border-gray-300 bg-gray-100 text-black opacity-60'
                }`}
              >
                <div className="font-bold">Open-Meteo</div>
                <div className="text-xs mt-1">Free, no key, reliable</div>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, weather_api: 'wttr.in' }))}
                className={`flex-1 py-3 px-4 rounded-xl border-2 text-sm transition-all ${
                  settings.weather_api === 'wttr.in'
                    ? 'border-green-500 bg-green-50 text-black'
                    : 'border-gray-300 bg-gray-100 text-black opacity-60'
                }`}
              >
                <div className="font-bold">wttr.in</div>
                <div className="text-xs mt-1">Free backup API</div>
              </button>
            </div>
            <p className={`text-xs mt-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Primary API for weather data. If it fails, the other is used as fallback automatically.
            </p>
          </div>

          {/* Save Button */}
          <button
            onClick={saveSettings}
            disabled={saving}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : '💾 Save Settings'}
          </button>
        </div>
      </div>

      {/* Test Weather API */}
      <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🧪 Test Weather API
        </h4>

        <div className="flex gap-3 mb-4">
          <select
            value={testCity}
            onChange={(e) => setTestCity(e.target.value)}
            className={`flex-1 px-4 py-2 rounded-lg border ${
              isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
          >
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
          <button
            onClick={testWeather}
            disabled={testing}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            {testing ? '⏳' : '🔍 Test'}
          </button>
        </div>

        {testResult && (
          <div className={`p-4 rounded-lg ${
            testResult.error
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
          }`}>
            {testResult.error ? (
              <p className="text-red-600 dark:text-red-400">❌ Error: {testResult.error}</p>
            ) : (
              <div className="flex items-center gap-4">
                <span className="text-4xl">{testResult.icon}</span>
                <div>
                  <p className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {testResult.city}: {testResult.temp_c}°C
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {testResult.condition} • Feels like {testResult.feels_like_c}°C
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    💧 {testResult.humidity}% humidity • 💨 {testResult.wind_kmh} km/h
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className={`rounded-xl p-5 border-2 border-blue-400 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
        <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ℹ️ How Weather Works
        </h4>
        <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <li>• <strong>API:</strong> wttr.in (free, no API key required)</li>
          <li>• <strong>Desktop:</strong> Shows under "Language Portal" text in sidebar</li>
          <li>• <strong>Mobile:</strong> Shows in top header bar (replaces avatar circle)</li>
          <li>• <strong>User Control:</strong> Users can enable/disable and set their city in Profile → Weather Settings</li>
          <li>• <strong>Storage:</strong> Settings in app_settings, user prefs in users collection</li>
        </ul>
      </div>
    </div>
  );
};

export default WeatherTab;
